import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, DatePicker, Form, InputNumber, Modal, Popconfirm, Radio, Select, Space, Table, Tabs, Typography, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { del, get, patch, post } from '../../lib/request'
import { DutyShift, DutyShiftBatchDeleteResult, errMsg, fmtTime } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'
import DutyShiftCalendar from './DutyShiftCalendar'

const { Text } = Typography

/** POST /admin/duty-shifts/rotate 的单条变更（dryRun 与实写同构） */
type RotateItem = {
  id: number
  startAt: string
  fromAdminUserId: number
  fromUsername: string | null
  toAdminUserId: number
  toUsername: string | null
}

/**
 * 值班排班（小时级，拍板5 + dutyshift_record_calendar_v1）：
 * 拆成「值班排班（今天+未来）/值班记录（已过去）」两个子页签；
 * 每页支持「切换为日历 / 切换为表单」双视图（表单=原 Table；日历=DutyShiftCalendar 月历）。
 * 顾问池切换 / 新增班次 / 周期生成 / 整体轮换在两视图下共用同一份 handler，行为一致。
 */
const DutyShiftTab: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = hasPermission(role, 'DUTY_EDIT')

  // rental_consultant（租房顾问）：顾问池切换 —— 招商顾问(operator) / 租房顾问(rental_consultant)，
  // 默认 operator 向后兼容；列表/下拉/新增/周期生成/整体轮换全部带 role 参数。
  const [dutyRole, setDutyRole] = useState<'operator' | 'rental_consultant'>('operator')

  // dutyshift_record_calendar_v1：两子页签（upcoming=今天+未来 / history=已过去）+ 视图模式（form/calendar）。
  const [rangeTab, setRangeTab] = useState<'upcoming' | 'history'>('upcoming')
  const [viewMode, setViewMode] = useState<'form' | 'calendar'>('form')
  // 日历受控年月（任一落在该月的 Dayjs）；切换面板即触发该月范围重拉。
  const [panelMonth, setPanelMonth] = useState<Dayjs>(dayjs())

  const [list, setList] = useState<DutyShift[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<DutyShift | 'new' | null>(null)
  const [form] = Form.useForm<{ adminUserId: number; range: [unknown, unknown] }>()
  const [saving, setSaving] = useState(false)

  // R5：周期生成（一人一天轮转、含周末；单条增删改不受影响）
  const [cycleOpen, setCycleOpen] = useState(false)
  const [cycleSaving, setCycleSaving] = useState(false)
  const [operators, setOperators] = useState<Array<{ id: number; username: string }>>([])
  const [cycleForm] = Form.useForm<{
    operatorIds: number[]
    startDate: { toISOString(): string }
    cycleDays?: number
    weeks?: number
    endDate?: { toISOString(): string }
  }>()

  // R6：整体轮换（某人休假→全员前轮一天；某人加班→全员后轮一天）
  const [rotateOpen, setRotateOpen] = useState(false)
  const [rotateSaving, setRotateSaving] = useState(false)
  const [rotatePreview, setRotatePreview] = useState<RotateItem[] | null>(null)
  const [rotateForm] = Form.useForm<{ from: { toISOString(): string }; direction: 'forward' | 'backward' }>()

  // dutyshift_batch_delete_replace_v1：按日期区间批量删除（dryRun 预览 → 实删）
  const [batchOpen, setBatchOpen] = useState(false)
  const [batchRange, setBatchRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [batchPreview, setBatchPreview] = useState<DutyShiftBatchDeleteResult['items'] | null>(null)
  const [batchPreviewLoading, setBatchPreviewLoading] = useState(false)
  const [batchDeleting, setBatchDeleting] = useState(false)

  // dutyshift_batch_delete_replace_v1：日历点任意一天 → 当日班次管理弹窗（逐条修改 / 勾选删除 / 当日新增）
  const [dayManage, setDayManage] = useState<{ day: Dayjs; shifts: DutyShift[] } | null>(null)
  const [dayChecked, setDayChecked] = useState<number[]>([])
  const [dayDeleting, setDayDeleting] = useState(false)

  // R6：顾问下拉数据源改用 /admin/duty-shifts/operators（DUTY_VIEW，全7角色）。
  // 原先调 /admin/users 是 USER_VIEW（仅 SA/A/LM），持 DUTY_EDIT 的招商总监必然 403、下拉为空。
  const loadOperators = useCallback(() => {
    get<Array<{ id: number; username: string }>>(`/admin/duty-shifts/operators?role=${dutyRole}`)
      .then((d) => setOperators(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, '主理人列表加载失败')))
  }, [dutyRole])

  const openCycle = () => {
    setCycleOpen(true)
    cycleForm.resetFields()
    loadOperators()
  }

  const openRotate = () => {
    setRotateOpen(true)
    setRotatePreview(null)
    rotateForm.resetFields()
    rotateForm.setFieldsValue({ direction: 'forward' })
  }

  // dutyshift_batch_delete_replace_v1：值班人 id → 姓名（复用列表页映射：未加载到降级裸编号）
  const operatorName = (id: number) => {
    const u = operators.find((o) => o.id === id)
    return u ? `${u.username}（#${id}）` : `#${id}`
  }

  /** 按日期删除：选定区间后自动 dryRun 预览（from=起日 00:00 本地，to=止日+1 天 00:00 本地，含头含尾按自然日） */
  const refreshBatchPreview = async (range: [Dayjs, Dayjs] | null) => {
    if (!range) {
      setBatchPreview(null)
      return
    }
    setBatchPreviewLoading(true)
    try {
      const res = await post<DutyShiftBatchDeleteResult>('/admin/duty-shifts/batch-delete', {
        from: range[0].startOf('day').toISOString(),
        to: range[1].add(1, 'day').startOf('day').toISOString(),
        role: dutyRole,
        dryRun: true,
      })
      setBatchPreview(res?.items ?? [])
    } catch (err: unknown) {
      setBatchPreview(null)
      message.error(errMsg(err, '预览生成失败'))
    } finally {
      setBatchPreviewLoading(false)
    }
  }

  const openBatchDelete = () => {
    setBatchOpen(true)
    setBatchRange(null)
    setBatchPreview(null)
    loadOperators()
  }

  /** 按日期删除：实删（同参数，dryRun 缺省） */
  const saveBatchDelete = async () => {
    if (!batchRange) return
    setBatchDeleting(true)
    try {
      const res = await post<DutyShiftBatchDeleteResult>('/admin/duty-shifts/batch-delete', {
        from: batchRange[0].startOf('day').toISOString(),
        to: batchRange[1].add(1, 'day').startOf('day').toISOString(),
        role: dutyRole,
      })
      message.success(`已删除 ${res?.count ?? 0} 条班次`)
      setBatchOpen(false)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '批量删除失败'))
    } finally {
      setBatchDeleting(false)
    }
  }

  /** 当日班次管理：勾选删除所选（复用既有 DELETE /:id 单删路径逐条删，全部完成后统一提示+刷新+关弹窗） */
  const deleteDayChecked = () => {
    if (!dayManage || dayChecked.length === 0) return
    const ids = [...dayChecked]
    Modal.confirm({
      title: `删除所选 ${ids.length} 条班次？`,
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        setDayDeleting(true)
        try {
          await Promise.all(ids.map((id) => del(`/admin/duty-shifts/${id}`)))
          message.success(`已删除 ${ids.length} 条班次`)
          setDayManage(null)
          fetchList()
        } catch (err: unknown) {
          message.error(errMsg(err, '删除失败'))
        } finally {
          setDayDeleting(false)
        }
      },
    })
  }

  /** 起始日 + 方向齐备时自动拉 dryRun 预览（不写库） */
  const refreshRotatePreview = async () => {
    const v = rotateForm.getFieldsValue()
    if (!v.from || !v.direction) {
      setRotatePreview(null)
      return
    }
    try {
      const res = await post<{ count: number; items: RotateItem[] }>('/admin/duty-shifts/rotate', {
        from: v.from.toISOString(),
        direction: v.direction,
        role: dutyRole,
        dryRun: true,
      })
      setRotatePreview(res?.items ?? [])
    } catch (err: unknown) {
      setRotatePreview(null)
      message.error(errMsg(err, '预览生成失败'))
    }
  }

  const saveRotate = async () => {
    const v = await rotateForm.validateFields()
    setRotateSaving(true)
    try {
      const res = await post<{ count: number }>('/admin/duty-shifts/rotate', {
        from: v.from.toISOString(),
        direction: v.direction,
        role: dutyRole,
      })
      message.success(`已轮换 ${res?.count ?? 0} 条班次（班次时段未变动）`)
      setRotateOpen(false)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '整体轮换失败'))
    } finally {
      setRotateSaving(false)
    }
  }

  const saveCycle = async () => {
    const v = await cycleForm.validateFields()
    setCycleSaving(true)
    try {
      const res = await post<{ count: number; replacedCount?: number }>('/admin/duty-shifts/generate-cycle', {
        operatorIds: v.operatorIds,
        startDate: v.startDate.toISOString(),
        cycleDays: v.cycleDays ?? v.operatorIds.length,
        role: dutyRole,
        ...(v.weeks ? { weeks: v.weeks } : {}),
        ...(v.endDate ? { endDate: v.endDate.toISOString() } : {}),
      })
      // dutyshift_batch_delete_replace_v1：替换语义——重叠旧班次被自动替换时提示条数
      const replacedCount = res?.replacedCount ?? 0
      message.success(
        replacedCount > 0
          ? `已生成 ${res?.count ?? 0} 条班次，替换旧班次 ${replacedCount} 条`
          : `已生成 ${res?.count ?? 0} 条班次（一人一天轮转，含周末）`
      )
      setCycleOpen(false)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '周期生成失败'))
    } finally {
      setCycleSaving(false)
    }
  }

  /**
   * dutyshift_record_calendar_v1：按子页签拼 from/to 区间。
   * - upcoming（值班排班）：from=今天00:00 → endAt gt 今天00:00，命中「今天未结束 + 未来」，
   *   顶部即当天、往下全未来（当天跨过去/现在的班次按「未全部结束」留此页，口径③）。
   * - history（值班记录）：to=今天00:00 → startAt lt 今天00:00，命中「今天之前全部已结束」，
   *   前端按 startAt 倒序展示（最近在上）。
   * 后端 GET / 区间语义是重叠判定：startAt lt to AND endAt gt from（duty-shifts.ts::router.get('/')）。
   */
  const fetchList = useCallback(() => {
    setLoading(true)
    const todayStart = dayjs().startOf('day')
    let url = `/admin/duty-shifts?role=${dutyRole}`
    if (rangeTab === 'upcoming') {
      url += `&from=${encodeURIComponent(todayStart.toISOString())}`
    } else {
      url += `&to=${encodeURIComponent(todayStart.toISOString())}`
    }
    get<DutyShift[]>(url)
      .then((d) => {
        const arr = Array.isArray(d) ? d : []
        // 后端统一 startAt 升序；history 页前端翻转为倒序（最近在上）
        setList(rangeTab === 'history' ? [...arr].reverse() : arr)
      })
      .catch((err) => message.warning(errMsg(err, '班次加载失败')))
      .finally(() => setLoading(false))
  }, [dutyRole, rangeTab])

  useEffect(() => { fetchList() }, [fetchList])
  // R6：列表「值班人」列与单条新增下拉都要用到顾问名，进页面即加载一次
  useEffect(() => { loadOperators() }, [loadOperators])

  const openEdit = (s?: DutyShift, presetDay?: Dayjs) => {
    if (s) {
      setEditing(s)
      // dutyshift_record_calendar_v1 沿用上一修复：RangePicker value 必须是 dayjs 对象（不是字符串），杜绝白屏回归
      form.setFieldsValue({ adminUserId: s.adminUserId, range: [dayjs(s.startAt), dayjs(s.endAt)] })
    } else {
      setEditing('new')
      form.resetFields()
      // 日历点「无班次的某天」新增：预填该日 09:00–18:00（dayjs 对象）
      if (presetDay) {
        form.setFieldsValue({ range: [presetDay.hour(9).minute(0), presetDay.hour(18).minute(0)] } as never)
      }
    }
  }

  /** 日历点单日：0 条直接新增预填该日 09:00–18:00；≥1 条一律弹「当日班次管理」弹窗 */
  const handlePickDay = (day: Dayjs, shifts: DutyShift[]) => {
    if (!canEdit) return // 只读角色（无 DUTY_EDIT）点日历不弹编辑框
    if (shifts.length === 0) {
      openEdit(undefined, day)
    } else {
      // dutyshift_batch_delete_replace_v1：单日多条不再静默，统一进当日管理弹窗（修改/勾选删除/新增）
      setDayManage({ day, shifts })
      setDayChecked([])
    }
  }

  const save = async () => {
    const v = await form.validateFields()
    const [start, end] = v.range as Array<{ toISOString(): string }>
    setSaving(true)
    try {
      if (editing === 'new') {
        // dutyshift_batch_delete_replace_v1：新增走替换语义，响应带被替换旧班次明细
        const res = await post<{ id?: number; replaced?: unknown[] }>('/admin/duty-shifts', {
          adminUserId: v.adminUserId,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          role: dutyRole,
        })
        const replaced = res?.replaced?.length ?? 0
        message.success(replaced > 0 ? `已保存，替换旧班次 ${replaced} 条` : '班次已新增')
      } else if (editing) {
        await patch(`/admin/duty-shifts/${editing.id}`, {
          adminUserId: v.adminUserId,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
        })
        message.success('班次已更新')
      }
      setEditing(null)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const columns: ColumnsType<DutyShift> = useMemo(() => [
    { title: '班次ID', dataIndex: 'id', key: 'id', render: (v: number) => `#${v}` },
    {
      title: '值班人',
      dataIndex: 'adminUserId',
      key: 'adminUserId',
      // R6：由裸 `#7` 改为 `张三（#7）`；顾问名未加载到时降级回裸编号，不影响列表可用
      render: (v: number) => {
        const u = operators.find((o) => o.id === v)
        return u ? `${u.username}（#${v}）` : `#${v}`
      },
    },
    { title: '开始时间', dataIndex: 'startAt', key: 'startAt', render: fmtTime },
    { title: '结束时间', dataIndex: 'endAt', key: 'endAt', render: fmtTime },
    ...(canEdit
      ? [{
          title: '操作',
          key: 'actions',
          render: (_: unknown, r: DutyShift) => (
            <Space size={4}>
              <Button size="small" style={{ minHeight: 32 }} onClick={() => openEdit(r)}>修改</Button>
              <Popconfirm
                title="删除该班次？"
                okText="删除"
                cancelText="取消"
                onConfirm={() =>
                  del(`/admin/duty-shifts/${r.id}`)
                    .then(() => { message.success('已删除'); fetchList() })
                    .catch((err: unknown) => message.error(errMsg(err, '删除失败')))
                }
              >
                <Button size="small" danger style={{ minHeight: 32 }}>删除</Button>
              </Popconfirm>
            </Space>
          ),
        }]
      : []),
  ], [operators, canEdit])

  /** 共用工具条（两子页签、两视图一致）：顾问池切换 + 新增/周期生成/整体轮换 + 视图切换按钮 */
  const toolbar = (
    <Space style={{ marginBottom: 14 }} wrap>
      {/* rental_consultant（租房顾问）：顾问池切换，默认招商顾问；切换即重拉列表与下拉 */}
      <Radio.Group
        value={dutyRole}
        onChange={(e) => setDutyRole(e.target.value as 'operator' | 'rental_consultant')}
        buttonStyle="solid"
      >
        <Radio.Button value="operator" style={{ minHeight: 44, lineHeight: '42px' }}>招商主理人</Radio.Button>
        <Radio.Button value="rental_consultant" style={{ minHeight: 44, lineHeight: '42px' }}>租房主理人</Radio.Button>
      </Radio.Group>
      {canEdit && (
        <Button type="primary" onClick={() => openEdit()} style={{ minHeight: 44 }}>
          新增班次
        </Button>
      )}
      {canEdit && (
        <Button onClick={openCycle} style={{ minHeight: 44 }}>
          周期生成
        </Button>
      )}
      {canEdit && (
        <Button onClick={openRotate} style={{ minHeight: 44 }}>
          整体轮换
        </Button>
      )}
      {canEdit && (
        <Button danger onClick={openBatchDelete} style={{ minHeight: 44 }}>
          按日期删除
        </Button>
      )}
      {/* dutyshift_record_calendar_v1：表单 / 日历 视图切换；点「切换为日历」后按钮文案取反 */}
      <Button
        onClick={() => setViewMode((m) => (m === 'form' ? 'calendar' : 'form'))}
        style={{ minHeight: 44 }}
        type={viewMode === 'calendar' ? 'primary' : 'default'}
      >
        {viewMode === 'form' ? '切换为日历' : '切换为表单'}
      </Button>
      <Text type="secondary" style={{ fontSize: 12 }}>
        派单引擎按小时班次选值班人；空窗时段降级为「次一有班时段轮派」
        {dutyRole === 'rental_consultant' ? '（当前：租房主理人池）' : ''}
      </Text>
    </Space>
  )

  /** 当前子页签的内容区：表单=Table；日历=DutyShiftCalendar（受控年月） */
  const content = viewMode === 'calendar' ? (
    <DutyShiftCalendar
      list={list}
      operators={operators}
      canEdit={canEdit}
      panelMonth={panelMonth}
      onPanelMonthChange={(d) => setPanelMonth(d)}
      onPickDay={handlePickDay}
    />
  ) : (
    <Table<DutyShift>
      rowKey="id"
      loading={loading}
      columns={columns}
      dataSource={list}
      scroll={{ x: 'max-content' }}
      style={{ background: '#fff', borderRadius: 12 }}
      pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
    />
  )

  return (
    <>
      <Tabs
        activeKey={rangeTab}
        onChange={(k) => setRangeTab(k as 'upcoming' | 'history')}
        items={[
          { key: 'upcoming', label: '值班排班', children: null },
          { key: 'history', label: '值班记录', children: null },
        ]}
      />
      {toolbar}
      {content}
      <Modal
        title={editing === 'new' ? '新增班次' : '修改班次'}
        open={!!editing}
        onOk={save}
        onCancel={() => setEditing(null)}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="adminUserId"
            label={dutyRole === 'rental_consultant' ? '值班租房主理人' : '值班招商主理人'}
            rules={[{ required: true, message: '请选择值班主理人' }]}
          >
            {/* R6：裸数字 ID 输入框 → 从后台账号里挑招商顾问；rental_consultant：按当前顾问池加载 */}
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: '100%', minHeight: 44 }}
              placeholder={dutyRole === 'rental_consultant' ? '选择租房主理人' : '选择招商主理人'}
              options={operators.map((o) => ({ value: o.id, label: `${o.username}（#${o.id}）` }))}
            />
          </Form.Item>
          <Form.Item
            name="range"
            label="班次时段（精确到小时）"
            rules={[{ required: true, message: '请选择起止时间' }]}
          >
            <DatePicker.RangePicker
              showTime={{ format: 'HH:00' }}
              format="YYYY-MM-DD HH:00"
              style={{ width: '100%', minHeight: 44 }}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="按周期生成班次"
        open={cycleOpen}
        onOk={saveCycle}
        onCancel={() => setCycleOpen(false)}
        okText="生成"
        cancelText="取消"
        confirmLoading={cycleSaving}
        destroyOnClose
      >
        <Form form={cycleForm} layout="vertical">
          <Form.Item
            name="operatorIds"
            label={dutyRole === 'rental_consultant' ? '参与轮值的租房主理人（按选择顺序轮转，一人一天）' : '参与轮值的招商主理人（按选择顺序轮转，一人一天）'}
            rules={[{ required: true, message: '请至少选择一名主理人' }]}
          >
            <Select
              mode="multiple"
              placeholder={dutyRole === 'rental_consultant' ? '选择租房主理人' : '选择招商主理人'}
              style={{ minHeight: 44 }}
              options={operators.map((o) => ({ value: o.id, label: `${o.username}（#${o.id}）` }))}
            />
          </Form.Item>
          <Form.Item
            name="startDate"
            label="起始日"
            rules={[{ required: true, message: '请选择起始日' }]}
          >
            <DatePicker style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
          <Form.Item
            name="cycleDays"
            label="周期天数（默认=参与人数，即一人一天、N 天一轮；含周末）"
          >
            <InputNumber min={1} max={30} style={{ width: '100%', minHeight: 44 }} placeholder="默认 = 主理人人数" />
          </Form.Item>
          <Form.Item name="weeks" label="生成周数（与结束日二选一）">
            <InputNumber min={1} max={52} style={{ width: '100%', minHeight: 44 }} placeholder="如 2 = 两周" />
          </Form.Item>
          <Form.Item name="endDate" label="结束日（与周数二选一）">
            <DatePicker style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            时间重叠的旧班次将被本次生成自动替换（操作日志可查）；生成后仍可用「修改/删除」单独调整某一天
          </Text>
        </Form>
      </Modal>
      <Modal
        title="按日期删除班次"
        open={batchOpen}
        onOk={saveBatchDelete}
        onCancel={() => setBatchOpen(false)}
        okText="确认删除"
        cancelText="取消"
        confirmLoading={batchDeleting}
        okButtonProps={{ danger: true, disabled: !batchRange || !batchPreview || batchPreview.length === 0 }}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="删除区间（按自然日，含头含尾）" required>
            <DatePicker.RangePicker
              value={batchRange}
              onChange={(v) => {
                const range = v?.[0] && v?.[1] ? ([v[0], v[1]] as [Dayjs, Dayjs]) : null
                setBatchRange(range)
                refreshBatchPreview(range)
              }}
              style={{ width: '100%', minHeight: 44 }}
            />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            仅删除当前主理人池（{dutyRole === 'rental_consultant' ? '租房主理人' : '招商主理人'}）区间内的班次，另一主理人池不受影响。
          </Text>
        </Form>
        {batchPreview && batchPreview.length > 0 && (
          <Table<DutyShiftBatchDeleteResult['items'][number]>
            rowKey="id"
            size="small"
            loading={batchPreviewLoading}
            columns={[
              { title: '值班人', dataIndex: 'adminUserId', key: 'adminUserId', render: operatorName },
              { title: '开始时间', dataIndex: 'startAt', key: 'startAt', render: fmtTime },
              { title: '结束时间', dataIndex: 'endAt', key: 'endAt', render: fmtTime },
            ]}
            dataSource={batchPreview}
            scroll={{ x: 'max-content' }}
            pagination={{ pageSize: 8, showTotal: (t) => `共 ${t} 条` }}
          />
        )}
        {batchPreview !== null && batchPreview.length === 0 && !batchPreviewLoading && (
          <Alert type="warning" showIcon message="该区间内没有可删除的班次" />
        )}
      </Modal>
      <Modal
        title={dayManage ? `${dayManage.day.format('YYYY-MM-DD')} 当日班次管理` : '当日班次管理'}
        open={!!dayManage}
        onCancel={() => setDayManage(null)}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
            <Button
              danger
              disabled={dayChecked.length === 0}
              loading={dayDeleting}
              style={{ minHeight: 44 }}
              onClick={deleteDayChecked}
            >
              删除所选{dayChecked.length > 0 ? `（${dayChecked.length}）` : ''}
            </Button>
            <Button
              type="primary"
              style={{ minHeight: 44 }}
              onClick={() => {
                const day = dayManage?.day
                setDayManage(null)
                if (day) openEdit(undefined, day)
              }}
            >
              新增班次
            </Button>
          </Space>
        }
        destroyOnClose
      >
        <Table<DutyShift>
          rowKey="id"
          size="small"
          columns={[
            {
              title: '',
              key: 'check',
              width: 44,
              render: (_: unknown, r: DutyShift) => (
                <Checkbox
                  checked={dayChecked.includes(r.id)}
                  onChange={(e) =>
                    setDayChecked((prev) =>
                      e.target.checked ? [...prev, r.id] : prev.filter((id) => id !== r.id)
                    )
                  }
                />
              ),
            },
            { title: '值班人', dataIndex: 'adminUserId', key: 'adminUserId', render: operatorName },
            {
              title: '班次时段',
              key: 'range',
              render: (_: unknown, r: DutyShift) => `${fmtTime(r.startAt)} – ${fmtTime(r.endAt)}`,
            },
            {
              title: '操作',
              key: 'actions',
              width: 72,
              render: (_: unknown, r: DutyShift) => (
                <Button
                  size="small"
                  style={{ minHeight: 32 }}
                  onClick={() => {
                    setDayManage(null)
                    openEdit(r)
                  }}
                >
                  修改
                </Button>
              ),
            },
          ]}
          dataSource={dayManage?.shifts ?? []}
          scroll={{ x: 'max-content' }}
          pagination={false}
        />
      </Modal>
      <Modal
        title="整体轮换班次"
        open={rotateOpen}
        onOk={saveRotate}
        onCancel={() => setRotateOpen(false)}
        okText="确认轮换"
        cancelText="取消"
        confirmLoading={rotateSaving}
        okButtonProps={{ disabled: !rotatePreview || rotatePreview.length === 0 }}
        destroyOnClose
      >
        <Form form={rotateForm} layout="vertical" onValuesChange={refreshRotatePreview}>
          <Form.Item name="from" label="从哪一天起调整" rules={[{ required: true, message: '请选择起始日' }]}>
            <DatePicker style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
          <Form.Item name="direction" label="轮换方向" rules={[{ required: true, message: '请选择轮换方向' }]}>
            <Radio.Group buttonStyle="solid" style={{ width: '100%' }}>
              <Radio.Button value="forward" style={{ minHeight: 44, lineHeight: '42px' }}>
                向前轮一天（有人休假）
              </Radio.Button>
              <Radio.Button value="backward" style={{ minHeight: 44, lineHeight: '42px' }}>
                向后轮一天（有人加班）
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 12 }}>
            只调整值班人归属，班次时段本身不变。该日期之后的所有班次会按当前轮值顺序整体位移一位。
          </Text>
        </Form>
        {rotatePreview && rotatePreview.length > 0 && (
          <>
            <Alert
              type="success"
              showIcon
              message="预览已生成，班次时段未发生任何变化"
              style={{ marginBottom: 12 }}
            />
            <Table<RotateItem>
              rowKey="id"
              size="small"
              columns={[
                { title: '班次时段', dataIndex: 'startAt', key: 'startAt', render: fmtTime },
                {
                  title: '值班人',
                  key: 'who',
                  render: (_: unknown, r: RotateItem) => (
                    <span>
                      <Text delete type="secondary">{r.fromUsername ?? `#${r.fromAdminUserId}`}</Text>
                      <span style={{ color: '#bfbfbf', padding: '0 6px' }}>→</span>
                      <Text strong>{r.toUsername ?? `#${r.toAdminUserId}`}</Text>
                    </span>
                  ),
                },
              ]}
              dataSource={rotatePreview}
              scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 8, showTotal: (t) => `共 ${t} 条` }}
            />
          </>
        )}
        {rotatePreview !== null && rotatePreview.length === 0 && (
          <Alert type="warning" showIcon message="该日期之后没有可轮换的班次" />
        )}
      </Modal>
    </>
  )
}

export default DutyShiftTab
